import {
    type CheckScmAuthForProjectResult,
    type PlainEnvironment,
    useCheckScmAuthForProject,
    useCreateEnvironmentFromProject,
} from "@/queries/environment-queries";
import type { PlainProject, PlainProjectEnvironmentClass } from "@/queries/project-queries";
import { useCallback } from "react";
import {
    useGetPreferredEnvironmentClassPerProject,
    useSetPreferredEnvironmentClassIdPerProject,
} from "@/hooks/use-preferred-env-class-per-project";
import { Code, ConnectError } from "@connectrpc/connect";

export type CreateEnvironmentResult =
    | { type: "success"; environment: PlainEnvironment }
    | { type: "authRequired"; authResult: CheckScmAuthForProjectResult };

export const useCreateEnvironmentFromProjectWithAuth = () => {
    const { preferredEnvironmentClassPerProject } = useGetPreferredEnvironmentClassPerProject();
    const setPreferredEnvironmentClassIdPerProject = useSetPreferredEnvironmentClassIdPerProject();
    const createEnvironment = useCreateEnvironmentFromProject();
    const checkAuth = useCheckScmAuthForProject();

    const handleCreateEnvironmentFromProject = useCallback(
        async (options: {
            project: PlainProject;
            preferredEnvClassId?: string;
            projectEnvironmentClasses: PlainProjectEnvironmentClass[];
            rememberChoice?: boolean;
            skipAuthCheck?: boolean;
        }): Promise<CreateEnvironmentResult | void> => {
            const { project, preferredEnvClassId, rememberChoice, skipAuthCheck } = options;
            // 1. if not authenticated, we need to show auth before creating the environment
            if (!skipAuthCheck) {
                const result = await checkAuth.mutateAsync({
                    project,
                    preferredEnvironmentClassId: preferredEnvClassId,
                    projectEnvironmentClasses: options.projectEnvironmentClasses,
                });
                const requiresAuth = result && !result.authenticationResult.authenticated;
                if (requiresAuth) {
                    return { type: "authRequired", authResult: result };
                }
            }

            // 2. if auth check was ok, let's create the environment
            if (preferredEnvClassId && rememberChoice) {
                const newPreferredEnvironmentClassPerProject = new Map<string, string>([
                    [project.id, preferredEnvClassId],
                    ...[...preferredEnvironmentClassPerProject].filter(([id]) => id !== project.id),
                ]);
                await setPreferredEnvironmentClassIdPerProject.mutateAsync(newPreferredEnvironmentClassPerProject);
            }

            try {
                const environment = await createEnvironment.mutateAsync({
                    project,
                    environmentClassId: preferredEnvClassId,
                });
                return { type: "success", environment };
            } catch (e) {
                // If there's an error and the preferred environment class ID matches the one we set in preferences,
                // we should remove it from the preferences to avoid getting the same error again
                // and the user can select a different one.
                if (
                    e instanceof ConnectError &&
                    (e.code === Code.FailedPrecondition || e.code === Code.NotFound) &&
                    preferredEnvClassId &&
                    preferredEnvironmentClassPerProject.get(project.id) === preferredEnvClassId
                ) {
                    const newPreferredEnvironmentClassPerProject = new Map<string, string>([
                        ...[...preferredEnvironmentClassPerProject].filter(([id]) => id !== project.id),
                    ]);
                    try {
                        await setPreferredEnvironmentClassIdPerProject.mutateAsync(
                            newPreferredEnvironmentClassPerProject,
                        );
                    } catch {
                        /* ignore */
                    }
                }
                throw e;
            }
        },
        [checkAuth, createEnvironment, preferredEnvironmentClassPerProject, setPreferredEnvironmentClassIdPerProject],
    );

    return {
        handleCreateEnvironmentFromProject,
        isLoading:
            checkAuth.isPending || createEnvironment.isPending || setPreferredEnvironmentClassIdPerProject.isPending,
        isSuccess: createEnvironment.isSuccess,
    };
};
